import { useCallback } from 'react';

import { Form } from '@arco-design/web-react';
import type { FormInstance } from '@arco-design/web-react';
import get from 'lodash/get';

import {
  type ComputeInventoryResourceType,
  ComputeVendorDeviceFields,
  type ComputeWorkloadType,
  flattenToFormPatch,
} from '../../capabilities/compute';
import { buildOptionFromVendorSelection, buildOptionMetaPatch } from '../../platform/option-meta';
import {
  VendorAndDeviceRadioList,
  VendorDeviceSelectionRequiredField,
  hasVendorDeviceSelection,
  parseGpuSoftwareFromBillDeviceDetail,
  resolveVendorDeviceRequiredValidateField,
} from '../vendorAndDeviceRadioList';
import type { VendorDeviceSelection } from '../vendorAndDeviceRadioList';
import type { VendorDeviceMeta } from '../vendorDevice';
import { readVendorDeviceMeta, vendorDeviceMetaFromSelection } from '../vendorDevice';

interface VendorDeviceSelectorProps {
  form: FormInstance;
  resourceCode: string;
  /** 仅展示该设备类型的计费项。 */
  deviceType?: string;
  vendorDevicePrefix: string;
  billModelId?: string;
  /** 交付类型 id，计价 resolve 必传 */
  deploymentTypeId?: string;
  architecture?: string;
  inventoryResourceType?: ComputeInventoryResourceType;
  workloadType?: ComputeWorkloadType;
  inventoryShowQuantity?: boolean;
  isContainerGpu?: boolean;
  requireGpuSoftware?: boolean;
  disabled?: boolean;
  /** 不请求厂商设备列表（fixed / 只读）；默认与 disabled 一致 */
  skipFetch?: boolean;
  /** 收紧底部外边距（网卡厂商设备下方紧跟网段列表） */
  compactBottom?: boolean;
  onPricingSelectionChange?: (selection: VendorDeviceSelection | null) => void;
}

function readSelection(meta?: VendorDeviceMeta | null): VendorDeviceSelection | null {
  if (!meta || !hasVendorDeviceSelection(meta)) return null;
  return {
    vendor: meta.vendor,
    device: meta.device,
    vendorID: meta.vendorID,
    deviceID: meta.deviceID,
    deviceType: meta.deviceType,
    billItemId: meta.billItemId,
    quantityUnit: meta.quantityUnit,
  };
}

/** 容器计价 GPU：从 detail 提取驱动/软件栈等，写入表单（与直建字段对齐，供 convert → deliveryMetadata） */
function buildContainerGpuSoftwarePatch(vendorDevicePrefix: string, selection: VendorDeviceSelection | null): Record<string, unknown> {
  const software = selection ? parseGpuSoftwareFromBillDeviceDetail(selection.detail) : undefined;
  return {
    [`${vendorDevicePrefix}.driverVersion`]: software?.driverVersion,
    [`${vendorDevicePrefix}.cudaVersion`]: software?.cudaVersion,
    [`${vendorDevicePrefix}.code`]: software?.code,
    [`${vendorDevicePrefix}.resourceType`]: software?.resourceType,
    [`${vendorDevicePrefix}.card.name`]: software?.resourceName,
  };
}

function mergePricingGpuValue(
  meta: VendorDeviceMeta | undefined,
  softwarePatch: Record<string, unknown>,
  vendorDevicePrefix: string,
  isContainerGpu: boolean,
): Record<string, unknown> | undefined {
  if (!meta) return undefined;
  if (!isContainerGpu) return { ...meta };
  const cardName = softwarePatch[`${vendorDevicePrefix}.card.name`];
  return {
    ...meta,
    driverVersion: softwarePatch[`${vendorDevicePrefix}.driverVersion`],
    cudaVersion: softwarePatch[`${vendorDevicePrefix}.cudaVersion`],
    code: softwarePatch[`${vendorDevicePrefix}.code`],
    resourceType: softwarePatch[`${vendorDevicePrefix}.resourceType`],
    ...(cardName ? { card: { name: cardName } } : {}),
  };
}

/** 厂商设备选择器：计价用单选列表，非计价用清单下拉（字段统一写入 vendorDevicePrefix） */
export function VendorDeviceSelector({
  form,
  resourceCode,
  deviceType,
  vendorDevicePrefix,
  billModelId,
  deploymentTypeId,
  architecture,
  inventoryResourceType,
  workloadType = 'VirtualMachine',
  inventoryShowQuantity = false,
  isContainerGpu = false,
  requireGpuSoftware = !isContainerGpu,
  disabled,
  skipFetch,
  compactBottom,
  onPricingSelectionChange,
}: VendorDeviceSelectorProps) {
  const value = Form.useWatch(vendorDevicePrefix, form) as VendorDeviceMeta | undefined;
  const effectiveSkipFetch = skipFetch ?? Boolean(disabled);

  const readPricingSelection = useCallback(
    // 动态计价前缀不一定逐项注册 Form.Item，必须读取完整 store 才能让必选校验识别已选值。
    () => readVendorDeviceMeta(form.getFields() as Record<string, unknown>, vendorDevicePrefix) as VendorDeviceSelection,
    [form, vendorDevicePrefix],
  );

  if (billModelId) {
    const requiredField = resolveVendorDeviceRequiredValidateField(vendorDevicePrefix);

    return (
      <VendorDeviceSelectionRequiredField fieldPrefix={vendorDevicePrefix} readSelection={readPricingSelection}>
        <VendorAndDeviceRadioList
          billModelId={billModelId}
          deploymentTypeId={deploymentTypeId}
          resourceCode={resourceCode}
          deviceType={deviceType}
          value={readSelection(value)}
          disabled={disabled}
          skipFetch={effectiveSkipFetch}
          compactBottom={compactBottom}
          onChange={(next) => {
            const meta = vendorDeviceMetaFromSelection(next);
            const built = buildOptionFromVendorSelection(next ?? undefined);
            const formValues = form.getFieldsValue() as Record<string, unknown>;
            const numberPath = `${vendorDevicePrefix}.number`;
            const preservedNumber = get(formValues, numberPath) ?? form.getFieldValue(numberPath);
            const softwarePatch = isContainerGpu ? buildContainerGpuSoftwarePatch(vendorDevicePrefix, next) : {};
            const gpuValue = mergePricingGpuValue(meta, softwarePatch, vendorDevicePrefix, isContainerGpu);
            const resolvedNumber = preservedNumber != null && preservedNumber !== '' ? preservedNumber : 1;
            const patch: Record<string, unknown> = {
              [vendorDevicePrefix]: gpuValue ? { ...gpuValue, number: resolvedNumber } : gpuValue,
              ...flattenToFormPatch(vendorDevicePrefix, gpuValue ? { ...gpuValue, number: resolvedNumber } : gpuValue),
              ...softwarePatch,
              ...(built ? buildOptionMetaPatch(built.key, built.option, formValues) : {}),
              [numberPath]: resolvedNumber,
            };
            form.setFieldsValue(patch);
            onPricingSelectionChange?.(next ?? null);
            void form.validate([requiredField]).catch(() => undefined);
          }}
        />
      </VendorDeviceSelectionRequiredField>
    );
  }

  if (!inventoryResourceType) return null;

  return (
    <ComputeVendorDeviceFields
      form={form}
      fieldPrefix={vendorDevicePrefix}
      resourceType={inventoryResourceType}
      workloadType={workloadType}
      architecture={architecture}
      showQuantity={inventoryShowQuantity}
      isContainer={isContainerGpu}
      requireGpuSoftware={requireGpuSoftware}
    />
  );
}
